'use client';

import { useEffect, useRef, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { Icon } from '@/app_components/common/Icon';
import { Image } from '@/app_components/common/Image';
import { StnVideo } from '@/app_components/common/StnVideo';
import type { AdTags } from '@/app_lib/services/ads/defaults';
import { cn } from '@/app_lib/utils/utils';

const videoStyles = tv({
  compoundVariants: [
    {
      class: {
        title: 'typography-level-6',
      },
      layout: 'default',
      modal: false,
    },
  ],
  defaultVariants: {
    layout: 'default',
    orientation: 'vertical',
  },
  slots: {
    base: 'flex gap-lg text-primary w-full',
    content: 'flex flex-col gap-sm flex-1',
    date: 'text-secondary typography-xs',
    description: '',
    player: '[--stn-player-borderRadius:8px] aspect-video w-full flex items-center justify-center',
    thumbnail: 'rounded-md aspect-video w-full object-cover',
    title: 'w-fit',
  },
  variants: {
    layout: {
      default: {
        base: 'max-w-[1096px]',
        title: 'font-semibold hover:text-primary-hover cursor-pointer line-clamp-2 lg:line-clamp-none',
      },
      hero: {
        content: 'lg:flex-row layout-grid-gap',
        description: 'lg:flex-1',
        player: 'md:[--stn-player-borderRadius:16px]',
        title: 'typography-level-1 font-bold font-inria-serif lg:flex-1',
      },
    },
    modal: {
      false: {},
      true: {
        description: 'text-secondary',
        player: 'lg:[--stn-player-borderRadius:8px] [--stn-player-titleBarColor:#ffffff]',
        title: 'typography-default font-semibold',
      },
    },
    orientation: {
      horizontal: {
        base: 'flex-row items-center',
        thumbnail: 'flex-1',
      },
      vertical: {
        base: 'flex-col items-start flex-1',
      },
    },
  },
});

type VideoStyles = VariantProps<typeof videoStyles>;

const Chip = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <span className={cn('py-xxxs px-xxs typography-xs rounded-sm', className)}>{children}</span>
);

const VideoStatus = ({ className = '' }: { className?: string }) => (
  <Chip className={cn('bg-blue-100 text-blue-600 font-medium', className)}>Watching</Chip>
);

const VideoDuration = ({ duration, className = '' }: { duration: string; className?: string }) => (
  <Chip className={cn('bg-black text-white font-medium', className)}>{duration}</Chip>
);

const VideoButton = ({ className = '' }: { className?: string }) => (
  <Icon
    className={cn('drop-shadow-[0_0_10px_rgba(17,17,17,1)] size-[30%]', className)}
    icon='CirclePlayFilled'
    mode='both'
    size='custom'
  />
);

export const VideoBase = ({
  children,
  layout,
  orientation,
  modal = false,
  className,
}: { children: React.ReactNode; modal?: boolean; className?: string } & VideoStyles) => (
  <div className={cn(videoStyles({ layout, modal, orientation }).base(), className)}>{children}</div>
);

export const VideoContent = ({ children, layout, orientation }: { children: React.ReactNode } & VideoStyles) => (
  <div className={videoStyles({ layout, orientation }).content()}>{children}</div>
);

// Static Image version
export const VideoThumbnail = ({
  src,
  layout,
  orientation,
  watching = false,
  modal = false,
  duration = '10:00',
  className,
  'data-index': index,
}: {
  src: string;
  watching?: boolean;
  modal?: boolean;
  duration?: string;
  className?: string;
  'data-index'?: number;
} & VideoStyles) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [useExtraLargeImage, setUseExtraLargeImage] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const { width } = el.getBoundingClientRect();
    const stnLargeImageWidth = 190;
    setUseExtraLargeImage(width > stnLargeImageWidth);
  }, []);

  return (
    <span
      className={cn(
        'group relative',
        !watching && 'cursor-pointer',
        videoStyles({ layout, modal, orientation }).thumbnail(),
        className,
      )}
      data-index={index}
      ref={ref}
    >
      <Image
        alt='Video Thumbnail'
        className={videoStyles({ layout, modal, orientation }).thumbnail()}
        fill
        src={useExtraLargeImage ? src.replace('/LG/', '/XL/') : src}
      />
      {watching ? (
        <VideoStatus className='absolute top-2 left-2 z-2' />
      ) : (
        <>
          <VideoButton className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white' />
          {duration && (
            <VideoDuration
              className='absolute bottom-2 right-2'
              duration={duration}
            />
          )}
        </>
      )}
      <div
        className={cn(
          'top-0 absolute bg-inverse/0 hover:bg-inverse/60 w-full h-full aspect-video z-1',
          watching && 'bg-inverse/60 border-3 border-blue-300',
          videoStyles({ layout, modal, orientation }).thumbnail(),
        )}
      />
    </span>
  );
};

// Video Player version - using STN Video Player
// TODO: There's an issue when a hero player is the same as a playlist video
// When clicking on the playlist video, the modal won't open another player. Clicking on a different video then back to the same video DOES work.
export const VideoPlayer = ({
  layout,
  orientation,
  modal = false,
  videoId,
  adTags,
  target,
}: { modal?: boolean; videoId: string; adTags?: AdTags[]; target?: string } & VideoStyles) => (
  <StnVideo
    adTags={adTags}
    aria-label='Video Player'
    className={videoStyles({ layout, modal, orientation }).player()}
    float={false}
    target={target}
    videoId={videoId}
  />
);

export const VideoTitle = ({
  layout,
  orientation,
  modal = false,
  className,
  title,
  playing = false,
  watching = false,
  'data-index': index,
}: {
  title: string;
  modal?: boolean;
  className?: string;
  playing?: boolean;
  watching?: boolean;
  'data-index'?: number;
} & VideoStyles) => (
  <span
    className={cn(
      videoStyles({ layout, modal, orientation }).title(),
      (watching || playing) && 'hover:text-primary cursor-default',
      className,
    )}
    data-index={index}
  >
    {title}
  </span>
);

export const VideoDescription = ({
  layout,
  orientation,
  modal = false,
  description,
}: { description: string; modal?: boolean } & VideoStyles) => (
  <span className={videoStyles({ layout, modal, orientation }).description()}>{description}</span>
);

export const VideoDate = ({
  layout,
  orientation,
  modal = false,
  date,
}: { date: string; modal?: boolean } & VideoStyles) => (
  <span className={videoStyles({ layout, modal, orientation }).date()}>{date}</span>
);
